/**
 * AP-8 — FM-WIN-06: J1939 Address Claim / NAME Monitor.
 * Datenquelle ist `getSnapshot('address-claim')`; Doppelklick zeigt die
 * NAME-Felder im gemeinsamen Detailfenster (wie AddressClaimWindow.cpp:327).
 */

import './viewsB.css';
import { createDataTable } from './dataTable';
import { createFloatingPanel } from './filters/FloatingPanel';
import { showDetailFields, type DetailField } from './Dm1Detail';
import { createLabel } from '../ui/controls';
import type { AppStore } from '../app/store';
import type { CanConnection } from '../app/connection';

const REFRESH_INTERVAL_MS = 100;

/** Feldnamen und Reihenfolge von `Isobus::NameFields()`. */
interface IsobusName {
  arbitraryAddressCapable: boolean;
  industryGroup: number;
  vehicleSystemInstance: number;
  vehicleSystem: number;
  function: number;
  functionInstance: number;
  ecuInstance: number;
  manufacturerCode: number;
  identityNumber: number;
}

interface ClaimNode {
  sa: number;
  count: number;
  name: IsobusName;
}

interface AddressClaimSnapshot {
  nodes: ClaimNode[];
}

function hex2(value: number): string {
  return value.toString(16).toUpperCase().padStart(2, '0');
}

export function createAddressClaimView(
  store: AppStore,
  connection: CanConnection,
): { show(): void } {
  const panel = createFloatingPanel('J1939 Address Claim / NAME Monitor');
  panel.element.classList.add('cm-panel-window--wide');

  const root = document.createElement('div');
  root.className = 'cm-viewb';

  const controls = document.createElement('div');
  controls.className = 'cm-viewb__row';
  const saFilter = document.createElement('select');
  saFilter.className = 'cm-combo';
  let saOptionsKey = '';
  controls.append(createLabel('Source Address:'), saFilter);

  const table = createDataTable({
    columns: ['SA', 'Cnt', 'IndGrp', 'VehSys', 'Func', 'FuncInst', 'EcuInst', 'MfrCode', 'Identity'],
    onSelect: () => undefined,
    emptyText: 'no address claims yet',
  });

  root.append(controls, table.element);
  panel.body.append(root);

  let rowMeta: ClaimNode[] = [];
  let lastRefresh = 0;
  let refreshQueued = false;

  function updateSaOptions(nodes: readonly ClaimNode[]): void {
    const sas = [...new Set(nodes.map((node) => node.sa))].sort((a, b) => a - b);
    const key = sas.join(',');
    if (key === saOptionsKey) return;
    saOptionsKey = key;
    const previous = saFilter.value;
    saFilter.replaceChildren();
    const all = document.createElement('option');
    all.value = 'all';
    all.textContent = 'All';
    saFilter.append(all);
    for (const sa of sas) {
      const option = document.createElement('option');
      option.value = String(sa);
      option.textContent = hex2(sa);
      saFilter.append(option);
    }
    saFilter.value = [...saFilter.options].some((option) => option.value === previous) ? previous : 'all';
  }

  async function refresh(force = false): Promise<void> {
    if (!panel.visible) return;
    const now = performance.now();
    if (!force && now - lastRefresh < REFRESH_INTERVAL_MS) {
      if (!refreshQueued) {
        refreshQueued = true;
        setTimeout(() => {
          refreshQueued = false;
          void refresh(true);
        }, REFRESH_INTERVAL_MS);
      }
      return;
    }
    lastRefresh = now;

    let snapshot: AddressClaimSnapshot;
    try {
      snapshot = (await connection.getSnapshot('address-claim')) as AddressClaimSnapshot;
    } catch {
      return;
    }

    updateSaOptions(snapshot.nodes);
    const filterValue = saFilter.value === 'all' ? null : Number(saFilter.value);
    const visible = snapshot.nodes
      .filter((node) => filterValue === null || node.sa === filterValue)
      .sort((a, b) => a.sa - b.sa);

    rowMeta = visible;
    table.setRows(
      visible.map((node) => [
        hex2(node.sa),
        String(node.count),
        String(node.name.industryGroup),
        String(node.name.vehicleSystem),
        String(node.name.function),
        String(node.name.functionInstance),
        String(node.name.ecuInstance),
        String(node.name.manufacturerCode),
        String(node.name.identityNumber),
      ]),
    );
  }

  function openDetail(row: number): void {
    const node = rowMeta[row];
    if (node === undefined) return;
    const saText = `0x${hex2(node.sa)}`;
    const fields: DetailField[] = [
      { label: 'Source Address', value: saText },
      { label: 'ArbitraryAddressCapable', value: node.name.arbitraryAddressCapable ? '1' : '0' },
      { label: 'IndustryGroup', value: String(node.name.industryGroup) },
      { label: 'VehicleSystemInstance', value: String(node.name.vehicleSystemInstance) },
      { label: 'VehicleSystem', value: String(node.name.vehicleSystem) },
      { label: 'Function', value: String(node.name.function) },
      { label: 'FunctionInstance', value: String(node.name.functionInstance) },
      { label: 'ECUInstance', value: String(node.name.ecuInstance) },
      { label: 'ManufacturerCode', value: String(node.name.manufacturerCode) },
      { label: 'IdentityNumber', value: String(node.name.identityNumber) },
    ];
    showDetailFields(`NAME Detail - SA ${saText}`, fields);
  }

  table.element.addEventListener('dblclick', (event) => {
    const row = (event.target as HTMLElement | null)?.closest('tr');
    if (row instanceof HTMLTableRowElement && row.parentElement?.tagName === 'TBODY') {
      openDetail(row.sectionRowIndex);
    }
  });

  saFilter.addEventListener('change', () => void refresh(true));
  store.on('frames:appended', () => void refresh());
  store.on('frames:cleared', () => void refresh(true));

  return {
    show(): void {
      panel.show();
      void refresh(true);
    },
  };
}
